import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import {
  Box,
  Card,
  Chip,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import React from 'react';
import ActionOnCommandIcon from '../../../svg-icons/ActionOnCommandIcon';
import SAVOrderIcon from '../../../svg-icons/SAVOrderIcon';

const SavDraftRecap = ({ savDraft, currency }) => {
  if (
    (!savDraft?.isSaved && savDraft?.lines?.length === 0) ||
    savDraft?.isArchived
  ) {
    return null;
  }

  const nameToDisplay =
    savDraft?.createdBy?.firstName + ' ' + savDraft?.createdBy?.lastName;

  return (
    <Paper
      elevation={16}
      key={savDraft?._id}
      sx={{
        padding: 0,
        marginBottom: 1,
        boxShadow: 'none',
      }}
    >
      <Card
        elevation={5}
        sx={{
          display: 'flex',
          gap: 2,
          padding: 1,
          alignItems: 'center',
          justifyContent: 'space-evenly',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <ActionOnCommandIcon fontSize="large" />
            <Typography component={'span'} sx={{ fontSize: '0.9em' }}>
              {nameToDisplay}
            </Typography>
            <Chip
              label={savDraft?.isPayable ? 'SAV Payant' : 'SAV Gratuit'}
              color={savDraft?.isPayable ? 'warning' : 'success'}
              variant="outlined"
            />

            <Typography component={'span'} variant="caption">
              {new Date(savDraft?.updatedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              })}
            </Typography>
          </Box>
          <Divider sx={{ mt: 1 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <Typography sx={{ fontSize: '0.9em' }}>
              <strong>Liste des pièces / colis :</strong>
            </Typography>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}
            >
              {savDraft?.lines?.map((line) => (
                <Box
                  key={line?._id}
                  sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}
                >
                  {typeof line?.isAllocated === 'boolean' &&
                    line.isValid === false && (
                      <Tooltip title="Référence produit invalide">
                        <ErrorIcon color="error" sx={{ fontSize: '1.2em' }} />
                      </Tooltip>
                    )}
                  {line?.isAllocated && (
                    <Tooltip title="Stock alloués">
                      <CheckCircleIcon
                        sx={{ fontSize: '1.2em' }}
                        color="success"
                      />
                    </Tooltip>
                  )}
                  {line?.isAllocated === false && line?.isValid !== false && (
                    <Tooltip title="Vérifiez les stocks et l'association des pièces avec le préparateur">
                      <ReportProblemIcon
                        sx={{ fontSize: '1.2em' }}
                        color="warning"
                      />
                    </Tooltip>
                  )}
                  <Typography sx={{ fontSize: '0.8em' }}>
                    {`${line?.quantity} x `}
                    <strong>{line?.internalProduct}</strong>{' '}
                    {!!line?.title && line?.title}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <Typography sx={{ fontSize: '0.9em' }}>
              <strong>Préparateur</strong>:{' '}
              {savDraft?.orderPicker || 'Non renseigné'}
            </Typography>
            <Typography sx={{ fontSize: '0.9em' }}>
              <strong>Commentaire</strong> :{' '}
              {savDraft?.comment || 'Non renseigné'}
            </Typography>
            {savDraft?.reason?.name && (
              <Typography component={'span'} sx={{ fontSize: '0.9em' }}>
                <strong>Raison</strong> : {savDraft?.reason?.name}
              </Typography>
            )}
            {savDraft?.isPayable && (
              <Typography component={'span'} sx={{ fontSize: '0.9em' }}>
                <strong>Montant TTC net</strong> : {savDraft?.amount}{' '}
                {getSymbolFromCurrency(currency)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: 1 }}>
            <Typography sx={{ fontSize: '0.7em' }}>
              <i>
                Sans actions effectuées, le brouillon et sa commande SAV seront
                supprimés le{' '}
                {(() => {
                  const expirationDate = new Date(savDraft?.updatedAt);
                  if (savDraft?.isSaved) {
                    expirationDate.setDate(expirationDate.getDate() + 2);
                  }
                  return expirationDate.toLocaleString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  });
                })()}{' '}
                en fin de journée
              </i>
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <SAVOrderIcon fontSize="medium" />
            <Chip
              label={`Brouillon de commande SAV`}
              color={
                savDraft?.status?.name?.code === 'CREATED'
                  ? 'success'
                  : savDraft?.status?.name?.code === 'ERRORED'
                    ? 'error'
                    : 'warning'
              }
              variant="outlined"
              size="small"
              sx={{ width: 'fit-content', borderRadius: '5px' }}
            />
          </Box>
          <Typography
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            {savDraft?.reference}
          </Typography>
        </Box>
      </Card>
    </Paper>
  );
};

export default SavDraftRecap;
